import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking; false = unauth; object = auth
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, [setUser, setLoading]);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.access_token) localStorage.setItem("an_token", data.access_token);
            setUser(data.user);
            return { success: true, user: data.user };
        } catch (e) {
            return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    }, [setUser]);

    const register = useCallback(async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            return { success: true, message: data.message };
        } catch (e) {
            return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            // Server logout may fail (network, expired token); we still clear
            // client state below. Log so it's visible in DevTools when debugging.
            console.warn("[auth] logout request failed (clearing client state anyway):", err?.message || err);
        }
        localStorage.removeItem("an_token");
        setUser(false);
    }, [setUser]);

    const value = useMemo(
        () => ({ user, loading, login, register, logout, refreshUser: fetchMe }),
        [user, loading, login, register, logout, fetchMe]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
